import React, { useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { FontFamily } from '../../../../constants/fonts';
import { formatUserName } from '../../../../utils/emailTouserName';

import VerticalBarChartCard from '../../components/VerticalBarChartCard';
import MembersSection from '../../components/MembersSection';
import MetricCard from '../../components/MetricCard';

import type {
  StatCardData,
  TeamWorkloadDistributionData,
  TeamWorkloadMemberData,
  TicketingTeamWorkloadPayload,
} from '../../components/types';

interface TeamWorkloadProps {
  loading: boolean;
  error: string | null;
  data: TicketingTeamWorkloadPayload | null;
  onRetry?: () => void;
  hasActiveFilters?: boolean;
}

/**
 * Placeholder MetricCards rendered while the overview row loads. The row is a
 * fixed flex row (not a responsive grid), so a constant count is enough to
 * reserve the correct space.
 */
const OVERVIEW_SKELETON_CARD_COUNT = 4;

const formatMetricValue = (
  value: number,
  unit: string,
): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  if (unit === 'hours') {
    return value.toFixed(1);
  }

  return value.toLocaleString();
};

const formatTrendValue = (
  value: number,
  unit: string,
): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  return value.toFixed(1);
};

const TeamWorkload: React.FC<TeamWorkloadProps> = ({
  loading,
  error,
  data,
  onRetry,
  hasActiveFilters = false,
}) => {
  const overviewCards = useMemo<StatCardData[]>(() => {
    return (data?.overview ?? []).map((item) => ({
      label: item.title,
      value:
        typeof item.value === 'number'
          ? formatMetricValue(
              item.value,
              item.unit ?? 'count',
            )
          : item.key === 'busiest_agent'
            ? formatUserName(String(item.value))
            : String(item.value),
      icon: 'groups',
      iconBg: '#EAF1FB',
      iconColor: '#005CBC',
      subLabel:
        item.key === 'active_agents'
          ? 'of 8 Sustain Team members'
          : undefined,
      trendText: item.trend
        ? formatTrendValue(
            item.trend.value,
            item.trend.unit,
          )
        : undefined,
      trendDirection: item.trend?.direction,
      trendImpact: item.trend?.impact,
      description: item.description,
    }));
  }, [data?.overview]);

  const distributionData =
    useMemo<TeamWorkloadDistributionData[]>(() => {
      return data?.ticketDistribution?.series ?? [];
    }, [data?.ticketDistribution?.series]);

  const members = useMemo<TeamWorkloadMemberData[]>(() => {
    const apiData = data?.ticketsByTeamMember ?? [];
    
    // Deduplicate team members by name and aggregate their metrics
    const memberMap = new Map<string, any>();
    
    apiData.forEach((item) => {
      const memberName = formatUserName(item.teamMember);
      const normalizedKey = memberName.toLowerCase().trim();
      
      if (memberMap.has(normalizedKey)) {
        // Aggregate data for duplicate team members
        const existing = memberMap.get(normalizedKey);
        existing.assignedTickets += item.assignedTickets;
        existing.openTickets += item.openTickets;
        existing.closedTickets += item.closedTickets;
        // For time metrics, keep the first value (don't average)
        // Time values are already properly calculated from backend
        // Recalculate resolution rate based on aggregated counts
        const totalClosedAndOpen = existing.assignedTickets;
        existing.resolutionRate = totalClosedAndOpen > 0 
          ? (existing.closedTickets / totalClosedAndOpen) * 100 
          : 0;
      } else {
        // First occurrence of this team member
        memberMap.set(normalizedKey, {
          teamMember: memberName,
          assignedTickets: item.assignedTickets,
          openTickets: item.openTickets,
          closedTickets: item.closedTickets,
          averageResolutionTimeValue: item.averageResolutionTime.value,
          averageResolutionTimeUnit: item.averageResolutionTime.unit,
          averageTimeInProgressValue: item.averageTimeInProgress.value,
          averageTimeInProgressUnit: item.averageTimeInProgress.unit,
          averageTimeEscalatedToITValue: item.averageTimeEscalatedToIT.value,
          averageTimeEscalatedToITUnit: item.averageTimeEscalatedToIT.unit,
          averageTimeEscalatedToTMVValue: item.averageTimeEscalatedToTMV.value,
          averageTimeEscalatedToTMVUnit: item.averageTimeEscalatedToTMV.unit,
          averageTimeSubmittedToADOValue: item.averageTimeSubmittedToADO.value,
          averageTimeSubmittedToADOUnit: item.averageTimeSubmittedToADO.unit,
          averageTimeWaitingOnUserValue: item.averageTimeWaitingOnUser.value,
          averageTimeWaitingOnUserUnit: item.averageTimeWaitingOnUser.unit,
          resolutionRate: item.resolutionRate,
        });
      }
    });
    
    return Array.from(memberMap.values());
  }, [data?.ticketsByTeamMember]);

  return (
    <View style={styles.content}>
      {/*
        No blocking "Loading..." text — every card below renders its own
        skeleton via its `loading` prop so the layout stays stable.
      */}
      {/* Per-card errors render inside each card; avoid duplicate page-level error banner. */}

      <View style={styles.overviewGrid}>
        {/*
          Overview cards are derived purely from the API response, so while
          loading there is nothing to map over. Render a fixed set of
          placeholder MetricCards to preserve the grid.
        */}
        {loading
          ? Array.from({ length: OVERVIEW_SKELETON_CARD_COUNT }).map((_, index) => (
              <View
                key={`overview-skeleton-${index}`}
                style={styles.overviewCardItem}
              >
                <MetricCard loading title="" value="" />
              </View>
            ))
          : overviewCards.map((card) => (
          <View
            key={card.label}
            style={styles.overviewCardItem}
          >
            <MetricCard
              title={card.label}
              value={card.value}
              subtitle={card.subLabel}
              description={card.description}
              trendText={card.trendText}
              trendDirection={card.trendDirection}
              trendImpact={
                card.trendImpact ?? undefined
              }
            />
          </View>
        ))}
      </View>

    <VerticalBarChartCard
      title="Ticket Distribution"
      chartType="stacked"
      loading={loading}
      skeletonBarCount={8}
      legendPosition="topRight"
      legend={[
        {
          label: 'Open',
          color: '#F59E0B',
        },
        {
          label: 'Closed',
          color: '#16A34A',
        },
      ]}
      data={distributionData.map(item => ({
        label: formatUserName(item.teamMember),
        valueLabel:
          item.assignedTickets.toString(),
        openCount: item.openTickets,
        closedCount: item.closedTickets,
        segments: [
          {
            label: 'Open',
            value: item.openTickets,
            color: '#F59E0B',
          },
          {
            label: 'Closed',
            value: item.closedTickets,
            color: '#16A34A',
          },
        ],
      }))}
      error={error}
      onRetry={onRetry}
      hasActiveFilters={hasActiveFilters}
    />

      <MembersSection members={members} loading={loading} error={error} onRetry={onRetry} hasActiveFilters={hasActiveFilters} />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 2,
  },

  metaError: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    marginTop: 6,
    marginBottom: 8,
  },

  overviewGrid: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginBottom: 14,
    gap: 12,
  },

  overviewCardItem: {
    flex: 1,
  },
});

export default TeamWorkload;